/**
 * Rule engine types — the contract between intake data and a structured plan.
 * Pure types; no runtime imports.
 */

// =============================================================================
// Inputs
// =============================================================================

export type Sex = 'male' | 'female' | 'other';

export type ExperienceLevel = 'beginner' | 'intermediate' | 'advanced';

export type PrimaryGoal =
  | 'fat_loss'
  | 'muscle_gain'
  | 'strength'
  | 'endurance'
  | 'general_fitness';

export type Equipment =
  | 'barbell'
  | 'dumbbells'
  | 'cables'
  | 'machines'
  | 'bands'
  | 'bodyweight';

export type MovementPattern = 'squat' | 'hinge' | 'push' | 'pull' | 'carry' | 'core' | 'cardio';

export type MuscleGroup =
  | 'quadriceps'
  | 'hamstrings'
  | 'glutes'
  | 'calves'
  | 'chest'
  | 'back'
  | 'shoulders'
  | 'biceps'
  | 'triceps'
  | 'core';

export interface Injury {
  body_part: string;
  severity: 'minor' | 'moderate' | 'severe' | 'unknown';
  notes?: string;
}

export interface Schedule {
  days_per_week: number;
  session_duration_min: number;
}

export interface ClientInput {
  id: string;
  age: number;
  sex: Sex;
  weight_kg: number;
  height_cm: number;
  experience_level: ExperienceLevel;
  primary_goal: PrimaryGoal;
  equipment_available: Equipment[];
  schedule: Schedule;
  injuries: Injury[];
  preferences?: string[];
}

export interface Exercise {
  id: string;
  name: string;
  /** Polish display name; falls back to `name` when missing. */
  name_pl?: string;
  /** German display name; falls back to `name` when missing. */
  name_de?: string;
  primary_muscle: MuscleGroup;
  secondary_muscles: MuscleGroup[];
  equipment_required: Equipment[];
  difficulty: 'beginner' | 'intermediate' | 'advanced';
  contraindications: string[];
  movement_pattern: MovementPattern;
}

/** Resolve the display name for an exercise in the requested locale. */
export function localizedExerciseName(ex: Exercise, locale: string): string {
  if (locale === 'pl' && ex.name_pl) return ex.name_pl;
  if (locale === 'de' && ex.name_de) return ex.name_de;
  return ex.name;
}

export interface GenerateOptions {
  weeks: number;
  start_date: string; // ISO date YYYY-MM-DD
}

// =============================================================================
// Outputs
// =============================================================================

export type Split = 'full_body' | 'upper_lower' | 'push_pull_legs';

export interface PrescribedExercise {
  exercise_id: string;
  exercise_name: string;
  movement_pattern: MovementPattern;
  primary_muscle: MuscleGroup;
  order_index: number;
  sets: number;
  reps: number;
  load_kg: number | null;
  rpe_target: number;
  rest_seconds: number;
  tempo: string;
  notes: string;
}

export interface PlanDay {
  day_number: number;
  focus: string; // e.g. "upper" | "lower" | "full body" | "push" | etc.
  target_duration_min: number;
  exercises: PrescribedExercise[];
}

export interface PlanWeek {
  week_number: number;
  is_deload: boolean;
  days: PlanDay[];
}

export interface Plan {
  client_id: string;
  weeks: PlanWeek[];
  split: Split;
  start_date: string;
  end_date: string;
  generated_by: string;
}

// =============================================================================
// Trace
// =============================================================================

export type RuleName =
  | 'volume'
  | 'frequency'
  | 'split'
  | 'exercise_selection'
  | 'load_prescription'
  | 'progression'
  | 'bayesian_progression'
  | 'deload';

/**
 * A single client's logged outcome for one exercise across one week.
 * Used by the Bayesian progression rule (M-002) — see docs/model-cards/M-002.
 *
 * NOTE: Layer 1 only. This object stays in Postgres; never reaches Layer 2.
 */
export interface WorkoutObservation {
  week_number: number;
  exercise_id: string;
  rpe_target: number;
  /** RPE the client actually reported. null = missed / not logged. */
  rpe_actual: number | null;
  /** Was every prescribed set completed? null = unknown. */
  completed_all_sets: boolean | null;
}

export interface RuleTraceEntry {
  rule: RuleName;
  input?: Record<string, unknown>;
  decision: string;
  reason: string;
}

// =============================================================================
// Internal context shared between rules
// =============================================================================

export interface VolumePrescription {
  /** Target weekly sets per muscle group (lower bound). */
  sets_per_muscle_per_week_min: number;
  /** Target weekly sets per muscle group (upper bound). */
  sets_per_muscle_per_week_max: number;
}

export interface FrequencyPrescription {
  /** Muscle groups touched per session. */
  muscle_groups_per_session: number;
}

export interface RuleContext {
  client: ClientInput;
  options: GenerateOptions;
  exercises: Exercise[];
  volume: VolumePrescription;
  frequency: FrequencyPrescription;
  split: Split;
  /** Exercises filtered by equipment + injury contraindications. */
  exercise_pool: Exercise[];
}
